import React, { useMemo } from 'react';
import { Tabs, Badge, Space } from 'antd';
import {
  ProjectOutlined, FileSearchOutlined, ShopOutlined,
} from '@ant-design/icons';
import { Projects } from './Project';
import { Inspections } from './Inspections';
import Vendors from './Vendors';
import { useCondoStore } from '../store/condoStore';
import { useAuth } from '../../../context/UnifiedAuthContext';
import type { Inspection, Project } from '../types';

/**
 * S19 — Admin Operations Hub (final sprint)
 * Consolidates Projects, Inspections, and Vendors & Contractors into one
 * tabbed hub, completing the menu consolidation story started in S11.
 *
 * Role visibility:
 *   - Board members: Projects tab only
 *   - Admins: all three tabs
 */
const AdminOperationsHub: React.FC = () => {
  const { user } = useAuth();
  const isAdmin =
    user?.role === 'admin' ||
    user?.role === 'super_admin_condo' ||
    user?.role === 'superadmin' ||
    user?.role === 'ADMIN' ||
    user?.role === 'SUPER_ADMIN_CONDO';

  const { projects = [], inspections = [] } = useCondoStore() as any;

  // Active / in-progress projects
  const activeProjects: number = useMemo(
    () =>
      (projects as Project[]).filter(
        p => p.status === 'IN_PROGRESS' || p.status === 'PLANNING',
      ).length,
    [projects],
  );

  // Scheduled or follow-up-required inspections
  const pendingInspections: number = useMemo(
    () =>
      (inspections as Inspection[]).filter(
        i => i.status === 'scheduled' || (i.followUpRequired && i.status !== 'cancelled'),
      ).length,
    [inspections],
  );

  const makeBadgeLabel = (
    icon: React.ReactNode,
    label: string,
    count: number,
    color = '#1890ff',
  ) => (
    <Space size={4}>
      {icon}
      <span>{label}</span>
      {count > 0 && (
        <Badge
          count={count}
          size="small"
          style={{ backgroundColor: color, marginLeft: 2 }}
        />
      )}
    </Space>
  );

  const items = [
    {
      key: 'projects',
      label: makeBadgeLabel(
        <ProjectOutlined />,
        'Projects',
        activeProjects,
        '#722ed1',
      ),
      children: <Projects />,
    },
    ...(isAdmin
      ? [
          {
            key: 'inspections',
            label: makeBadgeLabel(
              <FileSearchOutlined />,
              'Inspections',
              pendingInspections,
              '#fa8c16',
            ),
            children: <Inspections />,
          },
          {
            key: 'vendors',
            label: (
              <Space size={4}>
                <ShopOutlined />
                <span>Vendors &amp; Contractors</span>
              </Space>
            ),
            children: <Vendors />,
          },
        ]
      : []),
  ];

  return (
    <Tabs
      items={items}
      size="large"
      destroyInactiveTabPane={false}
      style={{ marginTop: -8 }}
    />
  );
};

export default AdminOperationsHub;
